import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';

import Modal from '@rover/components/src/OverflowTextModal/Modal';
import { Button, Flex, Text } from '@rover/kibble/core';

import TruncatedText, { TruncatedTextProps } from './TruncatedText';

type InfoBoxProps = {
  children?: ReactNode | ReactNode[];
  isTrainerProfileReordering: boolean;
};

type ChildrenOnlyProps = {
  children: ReactNode | ReactNode[];
};

type BoldTextProps = {
  children: string;
  isTrainerProfileReordering: boolean;
} & React.ComponentProps<typeof Text>;

type LinkProps = {
  children: string;
};

const InfoBoxContext = createContext({
  modalIsOpen: false,
  openModal: () => {},
  closeModal: () => {},
});

const InfoBox = ({ children, isTrainerProfileReordering }: InfoBoxProps): JSX.Element => {
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  const infoBoxContextValue = useMemo(
    () => ({
      modalIsOpen,
      openModal: () => setModalIsOpen(true),
      closeModal: () => setModalIsOpen(false),
    }),
    [modalIsOpen]
  );

  return (
    <InfoBoxContext.Provider value={infoBoxContextValue}>
      <Flex
        data-testid="infobox-component"
        flexDirection="column"
        background={isTrainerProfileReordering ? 'primary' : 'accent_secondary'}
        borderRadius="8px"
        padding={isTrainerProfileReordering ? '0x' : '4x'}
      >
        {children}
      </Flex>
    </InfoBoxContext.Provider>
  );
};

InfoBox.Icon = (props: ChildrenOnlyProps): JSX.Element => {
  return <Flex pb="3x">{props.children}</Flex>;
};

InfoBox.BoldText = ({
  children,
  isTrainerProfileReordering,
  ...props
}: BoldTextProps): JSX.Element => (
  <Text
    fontWeight="semibold"
    textColor={isTrainerProfileReordering ? 'primary' : 'secondary'}
    {...props}
  >
    {children}
  </Text>
);

const Link = (props: LinkProps): JSX.Element => {
  const { openModal } = useContext(InfoBoxContext);
  return (
    <Flex pt="4x">
      <Button
        onClick={openModal}
        padding="0x"
        variant="flat"
        data-testid="infobox-link"
        sx={{
          '&:hover, &:focus': { background: 'none' },
        }}
      >
        <Text fontWeight="semibold" textDecoration="underline">
          {props.children}
        </Text>
      </Button>
    </Flex>
  );
};

InfoBox.Link = Link;

InfoBox.Text = (props: TruncatedTextProps): JSX.Element => (
  <Flex justifyContent="flex-start">
    <TruncatedText numberOfLines={3} textColor={props.textColor}>
      {props.children}
    </TruncatedText>
  </Flex>
);

const InfoBoxModal = (props: ChildrenOnlyProps): JSX.Element => {
  const { modalIsOpen, closeModal } = useContext(InfoBoxContext);
  return (
    <Modal isOpen={modalIsOpen} onClose={closeModal}>
      {props.children}
    </Modal>
  );
};

InfoBox.Modal = InfoBoxModal;

export default InfoBox;
